import { DunningLevel, NgbOffcanvas, callStated, callStatedList, updateEachListItem, withListState } from '@aston/foundation';
import { PartialStateUpdater, patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { toObservable } from '@angular/core/rxjs-interop';
import { effect, inject, untracked } from '@angular/core';
import { Events } from '@ngrx/signals/events';
import { map, of, takeUntil } from 'rxjs';
import { Router } from '@angular/router';

import DunningActionRealizedPageComponent from '../../pages/dunning-action-realized-page/dunning-action-realized-page.component';
import { ActionModalPanel, DunningActionType, DunningTimelineListSortProp, ExternalProviderLifeTimeStatus } from '../../enums';
import { accountingDetailPageEvents } from '../../../accounting-module/stores/accounting-detail/events';
import { contactsPostUpdateEvents } from '../../../shared-module/stores/contacts/events';
import { CommentsListSortProp, WsEventType } from '../../../shared-module/enums';
import { DunningTimelineService, DunningActionsService } from '../../services';
import { PaymentSchedulesService } from '../../../accounting-module/services';
import { ReferentialStore } from '../../../shared-module/stores/referential';
import { SuperDebtorPageStore } from '../../../debtors-module/stores';
import { WebSocketService } from '../../../shared-module/services';
import { getDunningLevelsBitAndValue } from '../../functions';
import { ICommentItem } from '../../../shared-module/models';
import { AppConstants } from '../../../app.constants';
import { IDunningTimelineItem } from '../../models';

import { dunningTimelineEvents } from './events';
import { withTimelineExport } from './dunning-timeline.export.feature';


export const TIMELINE = 'timeline';

export const DunningTimelineStore = signalStore(
	{ providedIn: 'root', protectedState: false },

	withState({
		showAll: false,
		showCompleted: false,
		superDebtorId: null as number,
		selectedDunningLevels: [DunningLevel.None],
		selectedExternalStatuses: [ExternalProviderLifeTimeStatus.None],
	}),

	withListState<IDunningTimelineItem, DunningTimelineListSortProp, typeof TIMELINE>({
		defaultSort: [
			{ prop: DunningTimelineListSortProp.IS_REALIZED, isDesc: true },
			{ prop: DunningTimelineListSortProp.ACTION_DATE, isDesc: true },
			{ prop: DunningTimelineListSortProp.IS_SURVEY_OR_CASE_OPENING, isDesc: false },
			{ prop: DunningTimelineListSortProp.ORIGIN, isDesc: true },
			{ prop: DunningTimelineListSortProp.ID, isDesc: true },
		],
		defaultPageSize: AppConstants.DUNNING_TIMELINE_DEFAULT_PAGE_SIZE,
		collection: TIMELINE,
	}),

	withComputed(() => {
		const dunningLevels = inject(ReferentialStore).dunningLevelsConfig
		const payingCenterMode = inject(SuperDebtorPageStore).payingCenterModeEnabled

		return {
			dunningLevels,
			payingCenterMode,
		}
	}),

	withTimelineExport(),

	withMethods(store => {
		const schedules = inject(PaymentSchedulesService)
		const timelines = inject(DunningTimelineService)
		const actions = inject(DunningActionsService)
		const offcanvas = inject(NgbOffcanvas)
		const router = inject(Router)

		const loading$ = toObservable(store.timelineLoading)
		const loadTimeline = (append?: boolean) => {
			if (!store.superDebtorId()) {
				console.debug(`[timeline] ignore unintialized reload`)
				return Promise.resolve()
			}
			return callStatedList(timelines.getTimelineForSuperDebtor(
				store.superDebtorId(),
				store.payingCenterMode(),
				{
					...store.timelinePaging(),
					pageSize: store.showAll()
						? AppConstants.LIST_PAGE_SIZE_IGNORED
						: store.timelinePaging().pageSize,
					filters: store.timelineFilters(),
				},
				store.showCompleted(),
				getDunningLevelsBitAndValue(store.selectedDunningLevels(), store.dunningLevels())
			), store, TIMELINE, { append })
		}

		const loadComments = (item: IDunningTimelineItem) => {
			let source = of([] as ICommentItem[])

			// User Story 28350: Rajouter des commentaires à l'échéancier de paiement - Clôture échéancier
			if (item.type === DunningActionType.ClosePaymentSchedule) {
				const paging = {
					sorts: [{prop: CommentsListSortProp.CREATION_DATE, isDesc: true}],
					pageSize: AppConstants.LIST_PAGE_SIZE_IGNORED,
				}
				source = schedules.getComments(item.paymentScheduleId, paging).pipe(map(list => list.items))

			} else {
				source = actions.getActionComments(item.id)
			}

			return callStated(source, store).then(comments =>
				patchState(store, updateEachListItem((i: IDunningTimelineItem) =>
					i.id !== item.id ? i : ({ ...i, comments, commentChecked: true }), TIMELINE)))
		}

		const updateActionProps = (args: { dunningActionId: number, updater: PartialStateUpdater<IDunningTimelineItem> }) =>
			patchState(store, updateEachListItem((da: IDunningTimelineItem) => da.id !== args.dunningActionId ? da : { ...da, ...args.updater(da) }, TIMELINE))

		const openRealizedAction = (id: number, panel: ActionModalPanel = ActionModalPanel.Form) => {
			const ref = offcanvas.open(DunningActionRealizedPageComponent, {
				...AppConstants.DEFAULT_OFFCANVAS_OPTIONS,
				backdropClass: 'offcanvas-backdrop-zindex',
				panelClass: 'offcanvas-panel-zindex offcanvas-panel-xxlarge'
			})
			router.events.pipe(takeUntil(ref.closed)).subscribe(() => offcanvas.dismiss());
			const modalInstance: DunningActionRealizedPageComponent = ref.componentInstance
			modalInstance.activeTabId = `dunning-action-modal-tab-${panel}`
			modalInstance.params$.next({ id, inTimeline: true, emitCloseEvent: true })
		}

		return {
			loadTimeline,
			loadComments,
			updateActionProps,
			openRealizedAction,
			loadingAsObservable: () => loading$,
			toggleShowAll: () => patchState(store, { showAll: !store.showAll() }),
			selectDunningLevels: (selectedDunningLevels: DunningLevel[]) => patchState(store, { selectedDunningLevels }),
			toggleShowCompleted: (state: boolean | undefined) => patchState(store, { showCompleted: state !== undefined ? state : !store.showCompleted() }),
			reload: () => store.superDebtorId() ? loadTimeline() : Promise.resolve(),
			init: (superDebtorId: number, showAll?: boolean, showCompleted?: boolean) => {
				const reload = superDebtorId !== store.superDebtorId()
				patchState(store, { superDebtorId, showAll, showCompleted})
				return reload
					? loadTimeline()
					: Promise.resolve()
			},
		}
	}),

	withHooks({
		onInit(store, e = inject(Events)) {

			e.on(dunningTimelineEvents.updateActionProps).subscribe(({ payload }) => store.updateActionProps(payload))

			inject(WebSocketService).ofType(WsEventType.GlobalWorkflowCompleted).subscribe(_ => store.loadTimeline())
	
			e.on(
				dunningTimelineEvents.reload,
				// Reload timeline on AD change
				accountingDetailPageEvents.dunningLevelFormUpdated,
				// Bug 35578: Reload timeline on contact deletion
				contactsPostUpdateEvents.updated, contactsPostUpdateEvents.deleted,
			).subscribe(_ => store.reload())

			effect(() => {
				const first = untracked(store.timelineCallState) === 'init'
				store.timelinePaging()
				// then
				if (!first) untracked(() => store.loadTimeline(true))
			})

			effect(() => {
				const first = untracked(store.timelineCallState) === 'init'
				store.payingCenterMode()
				store.timelineFilters()
				store.showCompleted()
				store.showAll()
				// then
				if (!first) untracked(store.loadTimeline)
			})
		},
	})
);
export type DunningTimelineStore = InstanceType<typeof DunningTimelineStore>;

